import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, Empty, Flex, Popconfirm, Select, Tag, Typography, message } from 'antd';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../lib/api-client';
import { assignTechnician, listTechnicians, releaseTechnician, type JobCard } from './api';

const { Text } = Typography;

/**
 * Who is on this job. The advisor picks from people whose role can log time —
 * the API decides that, so a renamed role never empties the list.
 */
export function JobTechnicians({ job, onChanged }: { job: JobCard; onChanged: () => void }): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [picked, setPicked] = useState<string | undefined>(undefined);

  const { data: options = [], error } = useQuery({
    queryKey: ['job-technicians', job.uid],
    queryFn: () => listTechnicians(job.uid),
    // A refusal is an answer, not a blip: do not retry it.
    retry: (count, e) => !(e instanceof ApiError && e.status === 403) && count < 2,
  });
  // The session knows roles, not permissions, so the API is the authority on
  // who may assign. A 403 means this person only sees who is on the job.
  const mayAssign = !(error instanceof ApiError && error.status === 403);

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['job-technicians', job.uid] });
    onChanged();
  };
  const fail = (e: unknown, fallback: string): void => {
    void message.error(e instanceof ApiError ? e.message : fallback);
  };

  const assign = useMutation({
    mutationFn: (technicianUid: string) => assignTechnician(job.uid, { technicianUid }),
    onSuccess: () => {
      setPicked(undefined);
      refresh();
    },
    onError: (e: unknown) => {
      fail(e, t('workshop.assignFailed'));
    },
  });

  const release = useMutation({
    mutationFn: (technicianUid: string) => releaseTechnician(job.uid, technicianUid),
    onSuccess: refresh,
    onError: (e: unknown) => {
      fail(e, t('workshop.releaseFailed'));
    },
  });

  const available = options.filter((o) => !o.assigned);
  const chosen = available.find((o) => o.uid === picked);
  const closed = job.closedAt !== null;

  return (
    <Card title={t('workshop.fields.technicians')}>
      <Flex vertical gap={12}>
        {job.technicians.length === 0 && (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('workshop.noTechnicians')} />
        )}
        {job.technicians.map((tech) => (
          <Flex key={tech.uid} justify="space-between" align="center" gap={8} wrap>
            <Flex gap={8} align="center">
              <Text strong>{tech.name}</Text>
              {tech.running && <Tag color="processing">{t('workshop.clockRunning')}</Tag>}
            </Flex>
            {!closed && mayAssign && (
              <Popconfirm
                title={t('workshop.releaseTitle', { name: tech.name })}
                description={t('workshop.releaseConsequence')}
                okText={t('workshop.release')}
                cancelText={t('common.cancel')}
                onConfirm={() => {
                  release.mutate(tech.uid);
                }}
              >
                <Button size="large" loading={release.isPending && release.variables === tech.uid}>
                  {t('workshop.release')}
                </Button>
              </Popconfirm>
            )}
          </Flex>
        ))}

        {!closed && mayAssign && (
          <Flex vertical gap={8}>
            <Select
              size="large"
              showSearch
              optionFilterProp="label"
              placeholder={t('workshop.pickTechnician')}
              value={picked}
              onChange={setPicked}
              notFoundContent={t('workshop.noOneToAssign')}
              options={available.map((o) => ({
                value: o.uid,
                label: `${o.name} · ${o.gradeCode ?? t('workshop.noGrade')}`,
              }))}
            />
            {chosen !== undefined && chosen.gradeCode === null && (
              // Allowed, because a job should not stall on admin — but the
              // advisor learns now rather than at the till.
              <Alert type="warning" showIcon message={t('workshop.noGradeWarning')} />
            )}
            <Button
              type="primary"
              size="large"
              disabled={picked === undefined}
              loading={assign.isPending}
              onClick={() => {
                if (picked !== undefined) assign.mutate(picked);
              }}
            >
              {t('workshop.assign')}
            </Button>
          </Flex>
        )}
      </Flex>
    </Card>
  );
}
